<script lang="ts" setup>
import { computed, inject } from 'vue';
import { i18n } from '@/i18n';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import SimpleSelectInput from '@/corePagesModule/components/SimpleSelectInput/SimpleSelectInput.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { key } from '../pages/useRecommendOrderStore';
import { IRelatedTransaction } from '../domains/entities/relatedTransaction';

const props = defineProps<{
  businessId: string;
}>();

const t = i18n.t;
const recommendOrderStore = inject(key);
if (!recommendOrderStore) {
  throw new Error('store is undefined');
}

/**
 * 関連取引モーダルの受領月オフセットの選択肢
 */
const relatedTransactionsReceiptMonthOffsetOptions = computed(() => [t('1ヶ月前内示'), t('2ヶ月前内示'), t('3ヶ月前内示')]);

/**
 * 関連取引モーダルの受領月オフセットの表示値
 */
const relatedTransactionsReceiptMonthOffsetDisplayValue = computed(() => {
  return recommendOrderStore.state.relatedTransactionsReceiptMonthOffset + t('ヶ月前内示');
});

/**
 * 固定カラムのグリッドテンプレート列
 */
const fixedColumnsGridTemplate = computed(() => {
  return ['150px', '150px', '150px', '150px', '100px']; // 売上先コード, 売上先名, 送り先コード, 送り先名, 種類
});

/**
 * スクロール可能な月別カラムのグリッドテンプレート列
 */
const scrollableColumnsGridTemplate = computed(() => {
  if (!recommendOrderStore.state.relatedTransactionsData?.monthlyColumns) {
    return [];
  }
  return recommendOrderStore.state.relatedTransactionsData.monthlyColumns.map(() => '120px');
});

/**
 * テーブルのローディング状態
 */
const relatedTransactionsTableLoadingStatus = computed<LoadingStatus>(() => {
  if (recommendOrderStore.state.relatedTransactionsLoading) {
    return { type: 'loading' };
  }
  if (recommendOrderStore.state.relatedTransactionsError) {
    return { type: 'error', errorMessage: recommendOrderStore.state.relatedTransactionsError };
  }
  if (!recommendOrderStore.state.relatedTransactionsData || recommendOrderStore.state.relatedTransactionsData.transactions.length === 0) {
    return { type: 'zero-match' };
  }
  return { type: 'success' };
});

/**
 * 関連取引モーダルの受領月オフセットを更新する
 * @param value - 選択された値（例: "1ヶ月前内示" または "1 month ago forecast"）
 */
const updateRelatedTransactionsReceiptMonthOffset = async (value: string) => {
  await recommendOrderStore.updateRelatedTransactionsReceiptMonthOffset(props.businessId, value);
};

/**
 * 関連取引モーダルを閉じる
 */
const closeRelatedTransactionsModal = () => {
  recommendOrderStore.closeRelatedTransactionsModal();
};

/**
 * 販売実績の値を取得する
 * @param transaction - 関連取引データ
 * @param year - 年
 * @param month - 月
 */
const getSalesResultValue = (transaction: IRelatedTransaction, year: number, month: number): number | null => {
  const result = transaction.salesResults?.find((sr) => sr.targetYear === year && sr.targetMonth === month);
  return result?.amount ?? null;
};

/**
 * 内示の値を取得する
 * @param transaction - 関連取引データ
 * @param year - 年
 * @param month - 月
 */
const getForecastValue = (transaction: IRelatedTransaction, year: number, month: number): number | null => {
  const result = transaction.customerForecasts?.find((cf) => cf.targetYear === year && cf.targetMonth === month);
  return result?.amount ?? null;
};
</script>

<template>
  <InfoModal
    :modal-state="recommendOrderStore.state.relatedTransactionsModalState"
    :modal-width="1600"
    :next-button-label="t('閉じる')"
    :enable-outside-click="true"
    :no-description="true"
    @on-next="closeRelatedTransactionsModal"
    @on-back="closeRelatedTransactionsModal"
  >
    <template #title>{{ t('販売実績と内示の明細') }}</template>
    <div class="related-transactions-modal">
      <div class="related-transactions-modal__header-select">
        <SimpleSelectInput
          :index="100"
          select-box-name="RELATED_TRANSACTIONS_RECEIPT_MONTH_OFFSET"
          :model-value="relatedTransactionsReceiptMonthOffsetDisplayValue"
          :prop-options="relatedTransactionsReceiptMonthOffsetOptions"
          display-allow
          @update:model-value="updateRelatedTransactionsReceiptMonthOffset"
        />
      </div>
      <!-- テーブル表示 -->
      <div class="related-transactions-modal__table-wrapper">
        <!-- 固定カラム部分 -->
        <div class="related-transactions-modal__table-fixed">
          <EzTable
            :grid-template-columns="fixedColumnsGridTemplate"
            :loading-status="relatedTransactionsTableLoadingStatus"
            sticky-header
            max-height="80vh"
            class="related-transactions-modal__table"
          >
            <template #header>
              <EzHeader class="related-transactions-modal__header-cell">{{ t('売上先コード') }}</EzHeader>
              <EzHeader class="related-transactions-modal__header-cell">{{ t('売上先名') }}</EzHeader>
              <EzHeader class="related-transactions-modal__header-cell">{{ t('送り先コード') }}</EzHeader>
              <EzHeader class="related-transactions-modal__header-cell">{{ t('送り先名') }}</EzHeader>
              <EzHeader class="related-transactions-modal__header-cell">{{ t('種類') }}</EzHeader>
            </template>
            <template #body>
              <template
                v-for="transaction in recommendOrderStore.state.relatedTransactionsData?.transactions || []"
                :key="transaction.baseBusinessId"
              >
                <!-- 販売実績行 -->
                <EzTableRow :grid-template-columns="fixedColumnsGridTemplate" class="related-transactions-modal__row--sales">
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ transaction.customerNumber }}</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ transaction.customerName }}</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ transaction.salesDestinationNumber }}</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ transaction.salesDestinationName }}</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ t('販売実績') }}</EzCell>
                </EzTableRow>
                <!-- 内示行 -->
                <EzTableRow :grid-template-columns="fixedColumnsGridTemplate" class="related-transactions-modal__row--forecast">
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">&nbsp;</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">&nbsp;</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">&nbsp;</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">&nbsp;</EzCell>
                  <EzCell class="related-transactions-modal__cell" :content-padding="16">{{ t('内示') }}</EzCell>
                </EzTableRow>
              </template>
            </template>
          </EzTable>
        </div>
        <!-- スクロール可能な月別カラム部分 -->
        <div class="related-transactions-modal__table-scrollable">
          <EzTable
            :grid-template-columns="scrollableColumnsGridTemplate"
            :loading-status="relatedTransactionsTableLoadingStatus"
            sticky-header
            max-height="80vh"
            class="related-transactions-modal__table"
          >
            <template #header>
              <EzHeader
                v-for="(col, index) in recommendOrderStore.state.relatedTransactionsData?.monthlyColumns || []"
                :key="`month-${index}`"
                class="related-transactions-modal__header-cell"
              >
                {{ col.year }}/{{ String(col.month).padStart(2, '0') }}
              </EzHeader>
            </template>
            <template #body>
              <template
                v-for="(transaction, transIndex) in recommendOrderStore.state.relatedTransactionsData?.transactions || []"
                :key="transaction.baseBusinessId"
              >
                <!-- 販売実績行 -->
                <EzTableRow :grid-template-columns="scrollableColumnsGridTemplate" class="related-transactions-modal__row--sales">
                  <EzCell
                    v-for="(col, colIndex) in recommendOrderStore.state.relatedTransactionsData?.monthlyColumns || []"
                    :key="`sales-${transIndex}-${colIndex}`"
                    class="related-transactions-modal__cell"
                    :content-padding="16"
                    force-right
                    is-number
                  >
                    <span v-if="getSalesResultValue(transaction, col.year, col.month) !== null">
                      {{ getSalesResultValue(transaction, col.year, col.month)?.toLocaleString() }}
                    </span>
                  </EzCell>
                </EzTableRow>
                <!-- 内示行 -->
                <EzTableRow :grid-template-columns="scrollableColumnsGridTemplate" class="related-transactions-modal__row--forecast">
                  <EzCell
                    v-for="(col, colIndex) in recommendOrderStore.state.relatedTransactionsData?.monthlyColumns || []"
                    :key="`forecast-${transIndex}-${colIndex}`"
                    class="related-transactions-modal__cell"
                    :content-padding="16"
                    force-right
                    is-number
                  >
                    <span v-if="getForecastValue(transaction, col.year, col.month) !== null">
                      {{ getForecastValue(transaction, col.year, col.month)?.toLocaleString() }}
                    </span>
                  </EzCell>
                </EzTableRow>
              </template>
            </template>
          </EzTable>
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.related-transactions-modal {
  display: grid;
  gap: 16px;
  padding: 0;
  position: relative;
  margin-top: -12px; // 12px total space (24px gap - 12px margin = 12px)

  &__header-select {
    min-width: 150px;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  // テーブルのフォントサイズとセル高さを大きくする
  &__table {
    :deep(.table__header) {
      background-color: #eeeeee !important;
    }

    :deep(.table__header__content) {
      font-size: 16px !important;
      line-height: 20px !important;
      padding: 8px 16px !important;
      min-height: 36px;
      display: flex;
      align-items: center;
    }

    :deep(.table__cell__content) {
      font-size: 14px !important;
      line-height: 20px !important;
      padding: 8px 16px !important;
      min-height: 36px;
      display: flex;
      align-items: center;
    }

    :deep(.table__header) {
      min-height: 36px;
      border: none !important;
    }

    :deep(.table__cell) {
      min-height: 36px;
      border: none !important;
    }

    :deep(.table__cell--border--right),
    :deep(.table__cell--border--bottom) {
      border: none !important;
    }

    // 販売実績行は白背景
    :deep(.related-transactions-modal__row--sales .table__cell) {
      background-color: $semanticWhite !important;
    }

    // 内示行は#F5F5F5背景
    :deep(.related-transactions-modal__row--forecast .table__cell) {
      background-color: #f5f5f5 !important;
    }
  }

  &__table-wrapper {
    display: flex;
    max-height: 80vh;
    border: none;
    border-radius: 0;
    overflow: hidden;
  }

  &__table-fixed {
    flex-shrink: 0;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 80vh;
    border-right: 1px solid $borderDefault;
    background-color: $semanticWhite;
  }

  &__table-scrollable {
    flex: 1;
    overflow-x: auto;
    overflow-y: auto;
    max-height: 80vh;
    border-left: 1px solid $borderDefault;
    background-color: $backgroundPrimary;
  }
}

// Style the close button in the related transactions InfoModal to match the screenshot
:deep(.modal .modal__button--save--primary) {
  background-color: $semanticWhite !important;
  color: $semanticPrimary !important;
  border: 1px solid $semanticPrimary !important;
}
</style>
